import { useState, useCallback } from 'react';
import { Modal, Upload, Button, message, Alert, Progress, Table, Space, Typography, Divider, Input, Tag } from 'antd';
import { InboxOutlined, DownloadOutlined, UploadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

const { Dragger } = Upload;
const { Text, Title } = Typography;

export interface ValidationError {
  row: number;
  field: string;
  value: any;
  message: string;
}

export interface BulkUploadResult<T> {
  validRecords: T[];
  invalidRecords: any[];
  errors: ValidationError[];
  totalRecords: number;
  successCount: number;
  errorCount: number;
}

// Language-aware column mapping interface
export interface LanguageColumnMapping {
  [language: string]: {
    [internalKey: string]: string[]; // Array of possible header names in that language
  };
}

export interface BulkUploadModalProps<T> {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (result: BulkUploadResult<T>) => void;
  title: string;
  templateUrl?: string;
  templateFileName?: string;
  onDownloadTemplate?: () => void;
  validateRecord: (record: any, index: number) => ValidationError[];
  transformRecord?: (record: any) => T;
  columns?: Array<{
    key: string;
    title: string;
    required: boolean;
    hidden?: boolean;
    readOnly?: boolean;
  }>;
  sampleData?: any[];
  accept?: string;
  maxFileSize?: number; // in MB
  language?: string; // Language code for internationalization
  columnMappings?: LanguageColumnMapping; // Language-aware column mappings
  customDataProcessor?: (uploadResult: BulkUploadResult<any>) => any[]; // Custom data processor for display
}

// Language detection and column mapping utilities
const useLanguageDetection = (columnMappings: LanguageColumnMapping) => {
  const detectCSVLanguage = (headers: string[]): string => {
    // Check each supported language
    for (const [language, mappings] of Object.entries(columnMappings)) {
      const matchCount = headers.filter(header => 
        Object.values(mappings).some(possibleHeaders => 
          possibleHeaders.includes(header.toLowerCase())
        )
      ).length;
      
      // If more than 50% of headers match, consider it that language
      if (matchCount > headers.length * 0.5) {
        return language;
      }
    }
    
    // Try fuzzy matching for common variations
    const normalizedHeaders = headers.map(h => h.toLowerCase().replace(/[^a-z]/g, ''));
    
    // Check for Spanish indicators
    if (normalizedHeaders.some(h => h.includes('nombre') || h.includes('descripcion') || h.includes('pisos'))) {
      return 'es';
    }
    
    // Check for Portuguese indicators
    if (normalizedHeaders.some(h => h.includes('nome') || h.includes('descricao') || h.includes('andares'))) {
      return 'pt';
    }
    
    // Default to English if no clear match
    return 'en';
  };

  const mapHeadersToInternalKeys = (headers: string[], detectedLanguage: string): { [key: string]: string } => {
    const mapping: { [key: string]: string } = {};
    const languageMappings = columnMappings[detectedLanguage] || {};
    
    headers.forEach(header => {
      const normalizedHeader = header.toLowerCase().trim();
      
      // Find the internal key for this header
      for (const [internalKey, possibleHeaders] of Object.entries(languageMappings)) {
        if (possibleHeaders.includes(normalizedHeader)) {
          mapping[header] = internalKey;
          break;
        }
      }
      
      // If no mapping found, try to match by exact key (for English)
      if (!mapping[header] && detectedLanguage === 'en') {
        mapping[header] = header;
      }
    });
    
    return mapping;
  };

  const getLanguageDisplayName = (lang: string): string => {
    const languageNames: { [key: string]: string } = {
      'en': 'English',
      'es': 'Spanish',
      'pt': 'Portuguese'
    };
    return languageNames[lang] || lang.toUpperCase();
  };

  return { detectCSVLanguage, mapHeadersToInternalKeys, getLanguageDisplayName };
};

// UI Components
interface UploadStepProps {
  templateUrl?: string;
  sampleData: any[];
  maxFileSize: number;
  columns: any[];
  onDownloadTemplate: () => void;
  onFileUpload: (file: File) => boolean | Promise<boolean>;
  uploading: boolean;
  accept: string;
}

const UploadStep = ({ 
  templateUrl, 
  sampleData, 
  columns, 
  onDownloadTemplate, 
  onFileUpload, 
  uploading, 
  accept 
}: UploadStepProps) => {
  const { t } = useTranslation(['common']);
  
  return (
    <div>
      <Dragger
        name="file"
        multiple={false}
        accept={accept}
        beforeUpload={onFileUpload}
        showUploadList={false}
        disabled={uploading}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t('common:actions.dragDropText')}</p>
        <p className="ant-upload-hint">
          {t('common:actions.dragDropHint')}
        </p>
      </Dragger>

      {templateUrl || sampleData.length > 0 || onDownloadTemplate ? (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={onDownloadTemplate}
            size="large"
          >
            {t('common:actions.downloadTemplate')}
          </Button>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">{t('common:actions.templateFormatInfo')}</Text>
          </div>
        </div>
      ) : null}

      {columns.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Divider orientation="left">{t('common:actions.requiredFields')}</Divider>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '8px'
          }}>
            {columns.map((col: any) => (
              <div key={col.key} style={{ 
                padding: '12px 16px', 
                backgroundColor: col.required ? '#fff2f0' : '#f6ffed', 
                borderRadius: '8px',
                border: `1px solid ${col.required ? '#ffccc7' : '#b7eb8f'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ color: col.required ? '#cf1322' : '#389e0d' }}>
                    {col.title}
                  </Text>
                  {col.required && (
                    <span style={{ 
                      color: '#cf1322', 
                      fontWeight: 'bold', 
                      marginLeft: '4px' 
                    }}>
                      *
                    </span>
                  )}
                </div>
                <div style={{ 
                  padding: '2px 8px', 
                  borderRadius: '12px', 
                  backgroundColor: col.required ? '#ff4d4f' : '#52c41a',
                  fontSize: '10px',
                  color: 'white',
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}>
                  {col.required ? t('common:actions.required') : t('common:actions.optional')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ValidationStep = () => {
  const { t } = useTranslation(['common']);
  
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      <Progress type="circle" percent={100} status="active" />
      <Title level={4} style={{ marginTop: 24 }}>{t('common:actions.processingFile')}</Title>
      <Text type="secondary">{t('common:actions.pleaseWait')}</Text>
    </div>
  );
};

interface SummaryStepProps {
  uploadResult: BulkUploadResult<any> | null;
  columns: any[];
  headerMapping: { [key: string]: string };
  validateRecord: (record: any, index: number) => ValidationError[];
  transformRecord?: (record: any) => any;
  customDataProcessor?: (uploadResult: BulkUploadResult<any>) => any[];
}

const SummaryStep = ({ 
  uploadResult, 
  columns,
  headerMapping,
  validateRecord,
  transformRecord,
  customDataProcessor
}: SummaryStepProps) => {
  const { t } = useTranslation(['common']);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editingValues, setEditingValues] = useState<{ [key: string]: any }>({});
  
  if (!uploadResult) return null;
  
  // Function to update validation counts based on current editing values
  const updateValidationCounts = () => {
    if (!uploadResult) return;
    
    // Create a temporary copy of the data with current editing values
    const tempData = [...(uploadResult as any).originalValidRecords];
    
    // Apply current editing values to the temporary data
    Object.keys(editingValues).forEach(key => {
      const [rowIndex, columnKey] = key.split('-');
      const recordIndex = parseInt(rowIndex);
      const value = editingValues[key];
      
      if (tempData[recordIndex]) {
        const originalHeader = Object.keys(headerMapping).find(
          header => headerMapping[header] === columnKey
        );
        if (originalHeader) {
          tempData[recordIndex][originalHeader] = value;
        } else {
          tempData[recordIndex][columnKey] = value;
        }
      }
    });
    
    // Re-validate all records
    let validCount = 0;
    let invalidCount = 0;
    
    tempData.forEach((record, index) => {
      const transformedRecord = columns.reduce((acc, col) => {
        const originalHeader = Object.keys(headerMapping).find(
          header => headerMapping[header] === col.key
        );
        if (originalHeader && record[originalHeader] !== undefined) {
          acc[col.key] = record[originalHeader];
        } else if (record[col.key] !== undefined) {
          acc[col.key] = record[col.key];
        }
        return acc;
      }, {} as any);
      
      const errors = validateRecord(transformedRecord, index + 1);
      if (errors.length === 0) {
        validCount++;
      } else {
        invalidCount++;
      }
    });
    
    // Update the upload result counts
    uploadResult.successCount = validCount;
    uploadResult.errorCount = invalidCount;
  };
  
  // Update validation counts when editing values change
  useEffect(() => {
    updateValidationCounts();
  }, [editingValues]);

  // Transform the data to include both original and mapped values
  const transformedData = customDataProcessor 
    ? customDataProcessor(uploadResult)
    : (uploadResult as any).originalValidRecords?.map((record: any, index: number) => {
        // Check if this row has validation errors
        const rowErrors = uploadResult.errors.filter(e => e.row === index + 1);
        const hasErrors = rowErrors.length > 0;
        
        // Create a new record with row number and original values
        const transformedRecord: any = {
          rowNumber: index + 1,
          isErrorRow: hasErrors,
          errorCount: rowErrors.length,
          // Include the original CSV values for display
          ...record
        };
        
        return transformedRecord;
      }) || [];
  
  // Handle column-level errors (like missing required columns)
  const columnLevelErrors = uploadResult.errors.filter(e => e.row === 0);
  const hasColumnLevelErrors = columnLevelErrors.length > 0;
  
  // Use all original records directly - no fake error rows
  const allData = [...transformedData];
  
  const handleRowEdit = (rowIndex: number, columnKey: string, value: any) => {
    setEditingValues(prev => ({
      ...prev,
      [`${rowIndex}-${columnKey}`]: value
    }));
  };

  const handleRowSave = (rowIndex: number) => {
    const originalRecord = (uploadResult as any).originalValidRecords[rowIndex];
    const updatedRecord = { ...originalRecord };

    columns.forEach(col => {
      const editKey = `${rowIndex}-${col.key}`;
      if (editingValues[editKey] !== undefined) {
        // Find the original header that maps to this internal key
        const originalHeader = Object.keys(headerMapping).find(
          header => headerMapping[header] === col.key
        );
        if (originalHeader) {
          updatedRecord[originalHeader] = editingValues[editKey];
        } else {
          // If this column was missing, add it to the record
          updatedRecord[col.key] = editingValues[editKey];
        }
      }
    });

    // Update the original records
    (uploadResult as any).originalValidRecords[rowIndex] = updatedRecord;
    
    // Re-validate the updated record
    const transformedRecord = columns.reduce((acc, col) => {
      const originalHeader = Object.keys(headerMapping).find(
        header => headerMapping[header] === col.key
      );
      if (originalHeader && updatedRecord[originalHeader] !== undefined) {
        acc[col.key] = updatedRecord[originalHeader];
      } else if (updatedRecord[col.key] !== undefined) {
        acc[col.key] = updatedRecord[col.key];
      }
      return acc;
    }, {} as any);

    const errors = validateRecord(transformedRecord, rowIndex + 1);
    
    // Update validation errors for this row
    const existingErrors = uploadResult.errors.filter(e => e.row !== rowIndex + 1);
    uploadResult.errors = [...existingErrors, ...errors];
    
    // Update invalid/valid records
    if (errors.length === 0) {
      // Move from invalid to valid
      uploadResult.invalidRecords = uploadResult.invalidRecords.filter(r => r._row !== rowIndex + 1);
      if (transformRecord) {
        uploadResult.validRecords.push(transformRecord(transformedRecord));
      } else {
        uploadResult.validRecords.push(transformedRecord);
      }
    } else {
      // Update invalid record
      const invalidIndex = uploadResult.invalidRecords.findIndex(r => r._row === rowIndex + 1);
      if (invalidIndex >= 0) {
        uploadResult.invalidRecords[invalidIndex] = { ...transformedRecord, _row: rowIndex + 1 };
      } else {
        uploadResult.invalidRecords.push({ ...transformedRecord, _row: rowIndex + 1 });
      }
    }
    
    // Update counts
    uploadResult.successCount = uploadResult.validRecords.length;
    // errorCount is already correctly set to invalidRecords.length in the validation logic
    
    setEditingRow(null);
    setEditingValues({});
  };
  
  const handleRowCancel = () => {
    setEditingRow(null);
    setEditingValues({});
  };
  
  const startRowEditing = (rowIndex: number) => {
    setEditingRow(rowIndex);
    // Initialize editing values with current row data
    const record = transformedData[rowIndex];
    columns.forEach(col => {
      const originalHeader = Object.keys(headerMapping).find(
        header => headerMapping[header] === col.key
      );
      const currentValue = originalHeader ? record[originalHeader] : record[col.key];
      setEditingValues(prev => ({
        ...prev,
        [`${rowIndex}-${col.key}`]: currentValue
      }));
    });
  };
  
  const handleDeleteRow = (rowIndex: number) => {
    // Remove the row from the data
    const newData = [...transformedData];
    newData.splice(rowIndex, 1);
    // Update the upload result
    (uploadResult as any).validRecords.splice(rowIndex, 1);
    (uploadResult as any).originalValidRecords.splice(rowIndex, 1);
    (uploadResult as any).successCount = newData.length;
    (uploadResult as any).totalRecords = newData.length;
    // Force re-render
    setEditingValues({});
    setEditingRow(null);
  };
  
  // Create table columns for the data preview with proper mapping
  const dataColumns = [
    // Add row number column
    {
      title: '#',
      dataIndex: 'rowNumber',
      key: 'rowNumber',
      width: 60,
      fixed: 'left' as const,
      render: (_: any, __: any, index: number) => {
        const record = allData[index];
        return (
          <Text strong style={{ 
            fontSize: '12px', 
            color: record.isErrorRow ? '#ff4d4f' : '#666',
            backgroundColor: record.isErrorRow ? '#fff2f0' : 'transparent',
            padding: record.isErrorRow ? '4px 8px' : '0',
            borderRadius: record.isErrorRow ? '4px' : '0',
            border: record.isErrorRow ? '1px solid #ffccc7' : 'none'
          }}>
            {record.rowNumber}
            {record.isErrorRow && <span style={{ color: '#ff4d4f' }}> ⚠️</span>}
          </Text>
        );
      },
    },
    // Add actions column
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'left' as const,
      render: (_: any, __: any, index: number) => {
        const isEditing = editingRow === index;
        
        if (isEditing) {
          return (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                onClick={() => handleRowSave(index)}
                title="Save row"
                style={{ fontSize: '11px', padding: '0 8px' }}
              >
                Save
              </Button>
              <Button
                size="small"
                onClick={handleRowCancel}
                title="Cancel editing"
                style={{ fontSize: '11px', padding: '0 8px' }}
              >
                Cancel
              </Button>
            </Space>
          );
        }
        
        return (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => startRowEditing(index)}
              title="Edit entire row"
              style={{ color: '#1890ff' }}
            />
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteRow(index)}
              title="Delete row"
              style={{ color: '#ff4d4f' }}
            />
          </Space>
        );
      },
    },
    // Add mapped data columns
    ...columns.map((col: any) => ({
      title: (() => {
        // Check if this column is missing from the CSV
        const originalHeader = Object.keys(headerMapping).find(
          header => headerMapping[header] === col.key
        );
        const isMissingColumn = hasColumnLevelErrors && !originalHeader;
        const isOptionalColumn = !col.required;
        
        if (isMissingColumn) {
          if (isOptionalColumn) {
            // Show warning for optional missing columns
            return (
              <div style={{ color: '#faad14' }}>
                <div>{col.title}</div>
                <div style={{ fontSize: '10px', fontWeight: 'normal' }}>
                  ⚠️ Missing from CSV (Optional)
                </div>
              </div>
            );
          } else {
            // Show error for required missing columns
            return (
              <div style={{ color: '#cf1322' }}>
                <div>{col.title}</div>
                <div style={{ fontSize: '10px', fontWeight: 'normal' }}>
                  🚨 Missing from CSV
                </div>
              </div>
            );
          }
        }
        return col.title;
      })(),
      dataIndex: col.key,
      key: col.key,
      width: 150,
      render: (_: any, record: any, index: number) => {
        // Find the original header that maps to this internal key
        const originalHeader = Object.keys(headerMapping).find(
          header => headerMapping[header] === col.key
        );
        
        // Check if this column is missing (column-level error)
        const isMissingColumn = hasColumnLevelErrors && !originalHeader;
        const isOptionalColumn = !col.required;
        
        // Get the value to display from the original CSV data
        const displayValue = originalHeader ? record[originalHeader] : record[col.key];
        const isEditing = editingRow === index;
        const editKey = `${index}-${col.key}`;
        const editedValue = editingValues[editKey];
        
        // Check if this field has a row-level error
        const fieldErrors = uploadResult.errors.filter(e => 
          e.row === record.rowNumber && e.field === col.key
        );
        const hasRowError = fieldErrors.length > 0;
        const errorMessage = hasRowError ? fieldErrors[0].message : null;
        
        if (isEditing && !col.readOnly) {
          return (
            <div style={{ padding: '2px' }}>
              <Input
                value={editedValue !== undefined ? editedValue : displayValue}
                onChange={(e) => handleRowEdit(index, col.key, e.target.value)}
                size="small"
                style={{ 
                  border: isMissingColumn && !isOptionalColumn ? '2px solid #ff4d4f' : 
                         isMissingColumn && isOptionalColumn ? '2px solid #faad14' :
                         hasRowError ? '2px solid #ff4d4f' : '2px solid #1890ff',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              />
            </div>
          );
        }
        
        return (
          <div 
            style={{ 
              padding: '4px 8px', 
              backgroundColor: isMissingColumn && !isOptionalColumn ? '#fff2f0' : 
                             isMissingColumn && isOptionalColumn ? '#fffbe6' :
                             hasRowError ? '#fff2f0' : '#f8f9fa', 
              borderRadius: '4px',
              border: isMissingColumn && !isOptionalColumn ? '2px solid #ff4d4f' : 
                       isMissingColumn && isOptionalColumn ? '2px solid #faad14' :
                       hasRowError ? '1px solid #ffccc7' : '1px solid #e9ecef',
              minHeight: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              transition: 'all 0.2s',
              cursor: col.readOnly ? 'default' : 'pointer'
            }}
            onDoubleClick={() => {
              if (!col.readOnly) {
                setEditingRow(index);
                setEditingValues(prev => ({
                  ...prev,
                  [`${index}-${col.key}`]: displayValue
                }));
              }
            }}
            title={col.readOnly ? "Read-only field" : "Double-click to edit"}
          >
            <Text style={{ 
              fontSize: '12px', 
              color: isMissingColumn && !isOptionalColumn ? '#cf1322' : 
                     isMissingColumn && isOptionalColumn ? '#faad14' :
                     hasRowError ? '#cf1322' : '#495057',
              fontWeight: isMissingColumn ? '500' : 
                         hasRowError ? '500' : 'normal'
            }}>
              {editedValue !== undefined ? 
                (String(editedValue).length > 20 ? `${String(editedValue).substring(0, 20)}...` : String(editedValue)) :
                displayValue !== undefined && displayValue !== null ? 
                  (String(displayValue).length > 20 ? `${String(displayValue).substring(0, 20)}...` : String(displayValue)) :
                  <Text type="secondary" italic>undefined</Text>
              }
            </Text>
            {hasRowError && (
              <Text style={{ 
                fontSize: '10px', 
                color: '#cf1322',
                lineHeight: '1.2'
              }}>
                ⚠️ {errorMessage}
              </Text>
            )}
          </div>
        );
      },
    }))
  ];
  
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Alert
          message={t('common:actions.uploadSummary')}
          description={
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
              <div>{t('common:actions.totalRecords')}: <Text strong>{uploadResult.totalRecords}</Text></div>
              <div>{t('common:actions.validRecords')}: <Text strong type="success">{uploadResult.successCount}</Text></div>
              <div>{t('common:actions.invalidRecords')}: <Text strong type="danger">{uploadResult.errorCount}</Text></div>
            </div>
          }
          type={uploadResult.errorCount === 0 ? 'success' : 'warning'}
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Required vs Optional Fields Info */}
        <div style={{ marginBottom: 16 }}>
          <Divider orientation="left">
            <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
              📋 Field Information
            </Text>
          </Divider>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <Text strong style={{ color: '#ff4d4f', fontSize: '12px' }}>Required Fields:</Text>
              <div style={{ marginTop: 4 }}>
                {columns
                  .filter(col => col.required && col.hidden !== true) // Filter out hidden fields
                  .map(col => (
                    <Tag key={col.key} color="red" style={{ margin: '2px', fontSize: '10px' }}>
                      {col.title}
                    </Tag>
                  ))}
              </div>
            </div>
            <div>
              <Text strong style={{ color: '#52c41a', fontSize: '12px' }}>Optional Fields:</Text>
              <div style={{ marginTop: 4 }}>
                {columns
                  .filter(col => !col.required && col.hidden !== true) // Filter out hidden fields
                  .map(col => (
                    <Tag key={col.key} color="green" style={{ margin: '2px', fontSize: '10px' }}>
                      {col.title}
                    </Tag>
                  ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Data Preview Table - Airtable-like experience */}
        <div style={{ marginBottom: 24 }}>
          <Divider orientation="left">
            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
              📊 Data Preview ({uploadResult.totalRecords} records)
            </Text>
          </Divider>
          
          <div style={{ 
            backgroundColor: '#ffffff', 
            borderRadius: '8px', 
            border: '1px solid #e8e8e8',
            padding: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <Table
              dataSource={allData}
              columns={dataColumns}
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} records`,
                pageSizeOptions: ['5', '10', '20', '50'],
                style: { marginTop: '16px' }
              }}
              scroll={{ x: 'max-content', y: 300 }}
              rowKey={(_, index) => `row-${index}`}
              style={{ 
                backgroundColor: '#ffffff'
              }}
              rowClassName={(_, index) => {
                const record = allData[index];
                return record.isErrorRow ? 'error-row' : (index % 2 === 0 ? 'table-row-even' : 'table-row-odd');
              }}
              expandable={{
                expandedRowRender: (record) => {
                  if (record.isErrorRow) {
                    // Show row-level errors
                    const rowErrors = uploadResult.errors.filter(e => e.row === record.rowNumber);
                    if (rowErrors.length > 0) {
                      return (
                        <div style={{ 
                          backgroundColor: '#fff2f0', 
                          padding: '12px', 
                          margin: '8px 0',
                          borderRadius: '4px',
                          border: '1px solid #ffccc7'
                        }}>
                          <Text strong style={{ color: '#cf1322', marginBottom: '8px', display: 'block' }}>
                            ⚠️ Validation Errors for Row {record.rowNumber}:
                          </Text>
                          {rowErrors.map((error, errorIndex) => (
                            <div key={errorIndex} style={{ marginBottom: '4px' }}>
                              <Text style={{ color: '#cf1322', fontSize: '12px' }}>
                                <strong>{error.field}:</strong> {error.message}
                              </Text>
                            </div>
                          ))}
                        </div>
                      );
                    }
                  }
                  return null;
                },
                rowExpandable: (record) => record.isErrorRow && uploadResult.errors.some(e => e.row === record.rowNumber)
              }}
            />
          </div>
        </div>
        
        {/* No data fallback */}
        {allData.length === 0 && (
          <div style={{ marginBottom: 24 }}>
            <Divider orientation="left">
              <Text strong style={{ fontSize: '16px', color: '#666' }}>
                📊 No Data to Display
              </Text>
            </Divider>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px', 
              border: '1px solid #e9ecef',
              padding: '24px',
              textAlign: 'center'
            }}>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                No records were found in the uploaded file. Please check the file format and try again.
              </Text>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export function BulkUploadModal<T>({
  visible,
  onCancel,
  onConfirm,
  title,
  templateUrl,
  templateFileName = 'template.csv',
  onDownloadTemplate,
  validateRecord,
  transformRecord,
  columns = [],
  sampleData = [],
  accept = '.csv',
  maxFileSize = 5,
  columnMappings = {},
  customDataProcessor
}: BulkUploadModalProps<T>) {
  const { t } = useTranslation(['common']);
  const { detectCSVLanguage, mapHeadersToInternalKeys, getLanguageDisplayName } = useLanguageDetection(columnMappings);

  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult<T> | null>(null);
  const [currentStep, setCurrentStep] = useState<'upload' | 'validation' | 'summary'>('upload');
  const [headerMapping, setHeaderMapping] = useState<{ [key: string]: string }>({});

  const handleDownloadTemplate = useCallback(() => {
    if (onDownloadTemplate) {
      onDownloadTemplate();
    } else if (templateUrl) {
      const link = document.createElement('a');
      link.href = templateUrl;
      link.download = templateFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (sampleData.length > 0) {
      const csv = Papa.unparse(sampleData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = templateFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [onDownloadTemplate, templateUrl, templateFileName, sampleData]);

  const handleFileUpload = useCallback(async (file: File) => {
    if (file.size > maxFileSize * 1024 * 1024) {
      message.error(`File size must be less than ${maxFileSize}MB`);
      return false;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      message.error('Please upload a CSV file');
      return false;
    }

    setUploading(true);
    setCurrentStep('validation');

    try {
      const text = await file.text();
      const result = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        transform: (value) => value.trim()
      });

      if (result.errors.length > 0) {
        throw new Error('Error parsing CSV file');
      }

      const records = result.data as any[];
      if (records.length === 0) {
        throw new Error('CSV file contains no data');
      }

      // Detect language and map headers
      const headers = Object.keys(records[0]);
      const detectedLang = detectCSVLanguage(headers);
      
      const headerMapping = mapHeadersToInternalKeys(headers, detectedLang);
      setHeaderMapping(headerMapping); // Update headerMapping state
      
      // Validate that we have all required columns
      const requiredColumns = columns.filter(col => col.required).map(col => col.key);
      const missingColumns = requiredColumns.filter(key => 
        !Object.values(headerMapping).includes(key)
      );
      
      if (missingColumns.length > 0) {
        // Even with missing columns, we should show the actual CSV data
        // Transform records to use internal keys (some may be missing)
        const transformedRecords = records.map(record => {
          const transformed: any = {};
          Object.entries(record).forEach(([header, value]) => {
            const internalKey = headerMapping[header];
            if (internalKey) {
              transformed[internalKey] = value;
            }
          });
          return transformed;
        });

        const validationErrors: ValidationError[] = [];
        const validRecords: T[] = [];
        const invalidRecords: any[] = [];
        const allOriginalRecords: any[] = [];

        // Add column-level errors
        validationErrors.push({
          row: 0,
          field: 'columns',
          value: missingColumns.join(', '),
          message: `Missing required columns: ${missingColumns.join(', ')}`
        });

        // Process each record (they'll all be invalid due to missing columns)
        transformedRecords.forEach((record, index) => {
          const errors = validateRecord(record, index + 1);
          validationErrors.push(...errors);
          invalidRecords.push({ ...record, _row: index + 1 });
          allOriginalRecords.push(records[index]);
        });

        const resultData: BulkUploadResult<T> = {
          validRecords,
          invalidRecords,
          errors: validationErrors,
          totalRecords: records.length,
          successCount: 0,
          errorCount: records.length // Only count row-level errors, not column-level
        };

        // Store the original records for display
        (resultData as any).originalValidRecords = allOriginalRecords;

        setUploadResult(resultData);
        setCurrentStep('summary');
        setUploading(false);
        return false;
      }

      // Transform records to use internal keys
      const transformedRecords = records.map(record => {
        const transformed: any = {};
        Object.entries(record).forEach(([header, value]) => {
          const internalKey = headerMapping[header];
          if (internalKey) {
            transformed[internalKey] = value;
          }
        });
        return transformed;
      });

      const validationErrors: ValidationError[] = [];
      const validRecords: T[] = [];
      const invalidRecords: any[] = [];
      const allOriginalRecords: any[] = []; // Store ALL original CSV data for display

      transformedRecords.forEach((record, index) => {
        const errors = validateRecord(record, index + 1);
        if (errors.length > 0) {
          validationErrors.push(...errors);
          invalidRecords.push({ ...record, _row: index + 1 });
        } else {
          const transformedRecord = transformRecord ? transformRecord(record) : record;
          validRecords.push(transformedRecord);
        }
        // Store ALL original CSV records for display
        allOriginalRecords.push(records[index]);
      });

      const resultData: BulkUploadResult<T> = {
        validRecords,
        invalidRecords,
        errors: validationErrors,
        totalRecords: records.length,
        successCount: validRecords.length,
        errorCount: invalidRecords.length
      };

      // Store the original records for display
      (resultData as any).originalValidRecords = allOriginalRecords;

      setUploadResult(resultData);
      setCurrentStep('summary');
      
      // Language detection is handled silently - no need for user notification
    } catch (error) {
      message.error('Error processing file');
      console.error('File processing error:', error);
      
      // Create an error result to show in the UI
      const errorResult: BulkUploadResult<T> = {
        validRecords: [],
        invalidRecords: [],
        errors: [{
          row: 0,
          field: 'file',
          value: 'processing_error',
          message: 'Error processing file. Please check the file format and try again.'
        }],
        totalRecords: 0,
        successCount: 0,
        errorCount: 1
      };
      
      setUploadResult(errorResult);
      setCurrentStep('summary');
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  }, [validateRecord, transformRecord, maxFileSize, columns, columnMappings, detectCSVLanguage, mapHeadersToInternalKeys, getLanguageDisplayName]);

  const handleConfirm = useCallback(() => {
    if (uploadResult) {
      onConfirm(uploadResult);
    }
  }, [uploadResult, onConfirm]);

  const handleReset = useCallback(() => {
    setUploadResult(null);
    setCurrentStep('upload');
  }, []);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <UploadStep
            templateUrl={templateUrl}
            sampleData={sampleData}
            maxFileSize={maxFileSize}
            columns={columns}
            onDownloadTemplate={handleDownloadTemplate}
            onFileUpload={handleFileUpload}
            uploading={uploading}
            accept={accept}
          />
        );
      case 'validation':
        return <ValidationStep />;
      case 'summary':
        return (
          <SummaryStep
            uploadResult={uploadResult}
            columns={columns}
            headerMapping={headerMapping}
            validateRecord={validateRecord}
            transformRecord={transformRecord}
            customDataProcessor={customDataProcessor}
          />
        );
      default:
        return (
          <UploadStep
            templateUrl={templateUrl}
            sampleData={sampleData}
            maxFileSize={maxFileSize}
            columns={columns}
            onDownloadTemplate={handleDownloadTemplate}
            onFileUpload={handleFileUpload}
            uploading={uploading}
            accept={accept}
          />
        );
    }
  };

  const canProceed = uploadResult && uploadResult.successCount > 0;
  const hasErrors = uploadResult && uploadResult.errorCount > 0;
  const hasColumnLevelErrors = uploadResult && uploadResult.errors.some(e => e.row === 0);

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={
        <Space>
          {currentStep === 'summary' && (
            <>
              <Button onClick={handleReset}>
                {t('common:actions.uploadAnotherFile')}
              </Button>
              <Button onClick={onCancel}>
                {t('common:actions.cancel')}
              </Button>
            </>
          )}
          {currentStep === 'summary' && canProceed && (
            <Button 
              type="primary" 
              onClick={handleConfirm}
              icon={<UploadOutlined />}
            >
              {hasErrors ? t('common:actions.uploadValidRecordsOnly') : t('common:actions.uploadAllRecords')}
            </Button>
          )}
          {currentStep === 'summary' && !canProceed && hasColumnLevelErrors && (
            <Button 
              type="primary" 
              onClick={handleDownloadTemplate}
              icon={<DownloadOutlined />}
            >
              Download Template
            </Button>
          )}
        </Space>
      }
    >
      {renderStepContent()}
    </Modal>
  );
}

// Export internal components for reuse
export { UploadStep, ValidationStep, SummaryStep };
